import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useGenerationWatch, type GenerationPrefill } from '@entities/Generation';
import { useUserModulesPageQuery } from '@entities/Module';
import { useAuthStore } from '@entities/User';
import { fetchFreshWords, useFreshWordsQuery, wordKeys } from '@entities/Word';
import { useDashboardStatsQuery } from '@features/Dashboard';
import { EmailVerifyBanner } from '@features/EmailVerify';
import { LibraryGrid } from '@features/Library';
import { freshCounts, freshStateSet, WordsSection } from '@features/MyWords';
import { startCramSession } from '@features/Session';
import { trackEvent } from '@shared/analytics';
import { buildCramPath, paths, type ComposerNavigationState, type WordsReturnState } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, ChevronLeftIcon, ChevronRightIcon, LibraryIcon, PlayIcon, PlusIcon, toast } from '@shared/ui';
import { GenerationsSection } from '@widgets/GenerationsList';
import { HomeComposer, type HomeComposerHandle } from '@widgets/HomeComposer';
import { useIsSidebarMobile, useSidebarStore } from '@widgets/Sidebar';
import { ModulesShelf } from './ModulesShelf';
import { MyModules } from './MyModules';
import { ProgressStrip } from './ProgressStrip';
import { WordsToolbar } from './WordsToolbar';

/** Модули для строки создания — тот же запрос, что был на главной (ТЗ § 9): чип и меню модулей берут его. */
const COMPOSER_MODULES = { page: 1, size: 100, status: 'available' } as const;

/** main — модули и слова; modules — все модули с поиском и удалёнными (телефон); ready — готовые наборы. */
type View = 'main' | 'modules' | 'ready';

/**
 * «Слова и модули» (2026-10-23; без вкладок — вариант B, решение владельца 2026-10-28): модули и все слова ученика на
 * одной странице. Телефон — полка модулей со свайпом, под ней «Слова»; компьютер — модули колонкой слева, слова справа.
 * После «Создать» запись нажатия встаёт наверх «Слов» с «Тренировать новые», а сами слова — первыми и подсвечены.
 * Внизу — липкая строка создания карточки со всей обвязкой. Готовые наборы — `/words?tab=ready`, все модули на
 * телефоне — `/words?view=modules` (удалённые — `?filter=deleted`); старые `/units` и `/library` ведут сюда.
 */
export default function WordsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const isMobile = useIsSidebarMobile();
  // Лист «Ещё» на телефоне закрывает полосу: пока он открыт, итог генерации не принимается (ТЗ главной § 8).
  const moreOpen = useSidebarStore((s) => s.moreOpen);
  const user = useAuthStore((s) => s.user);
  const modulesQuery = useUserModulesPageQuery(COMPOSER_MODULES);
  const modules = modulesQuery.data?.items ?? [];
  const stats = useDashboardStatsQuery();
  const plan = stats.data?.plan ?? null;
  const qc = useQueryClient();
  const view: View =
    params.get('tab') === 'ready'
      ? 'ready'
      : isMobile && (params.get('view') === 'modules' || params.get('filter') === 'deleted')
        ? 'modules'
        : 'main';
  const headRef = useRef<HTMLElement>(null);
  const dockRef = useRef<HTMLElement>(null);
  const composerRef = useRef<HomeComposerHandle>(null);

  // «+ Карточка», «Добавить карточку» в модуле, новый модуль ведут сюда с флагом focusComposer (ТЗ главной § 5):
  // берём его по разу на переход и стираем — иначе «Назад» и перезагрузка снова поставили бы фокус. Ключ перехода в
  // ref переживает повтор эффекта в StrictMode; второй переход, уже стоя на «Словах и модулях», тоже сработает.
  const handledKey = useRef<string | null>(null);
  // Модуль и слово с другой страницы ждут, пока модуль появится в списке модулей: строка выбирает модуль только из него.
  const [composerTarget, setComposerTarget] = useState<{ moduleId: string; word: string } | null>(null);
  // Вернулись из тренировки — выбран модуль, который тренировали (ждёт список модулей так же).
  const [returnModuleId, setReturnModuleId] = useState<string | null>(
    () => (location.state as Partial<WordsReturnState> | null)?.wordsModuleId ?? null,
  );
  // Знакомство «Свои слова» (2026-11-02) возвращает сюда адресом `?compose=<модуль>` — state возврат после
  // знакомства не несёт. Параметр берём один раз и стираем, как focusComposer.
  useEffect(() => {
    const target = params.get('compose');
    if (target === null) return;
    const next = new URLSearchParams(params);
    next.delete('compose');
    setParams(next, { replace: true });
    if (target) setComposerTarget({ moduleId: target, word: '' });
    else window.setTimeout(() => composerRef.current?.focusField(), 0);
  }, [params, setParams]);

  useEffect(() => {
    const state = location.state as ComposerNavigationState | null;
    if (!state?.focusComposer || handledKey.current === location.key) return;
    handledKey.current = location.key;
    const { focusComposer: _consumed, composerModuleId, composerWord, ...rest } = state;
    navigate(location.pathname + location.search, { replace: true, state: Object.keys(rest).length ? rest : null });
    if (composerModuleId) setComposerTarget({ moduleId: composerModuleId, word: composerWord ?? '' });
    else window.setTimeout(() => composerRef.current?.focusField(), 0);
  }, [location.state, location.key, location.pathname, location.search, navigate]);

  // Подзаголовок про строку создания — только когда строка открыта: при неподтверждённой почте — без неё (ТЗ § 11).
  // Строка сверху (2026-10-28) — запасы снизу под сигнал и ряд ручной карточки больше не нужны.
  const composerOpen = !!user?.emailVerified || !!user?.guestCardAvailable;

  // Выбранный модуль (подробности — у pickModule ниже).
  const [picked, setPicked] = useState<{ id: string; title: string } | null>(null);

  // «Ввести снова» и «Добавить вручную» из записи — в строку создания (ТЗ § 22.6).
  const [prefill, setPrefill] = useState<GenerationPrefill | null>(null);
  useEffect(() => {
    if (!composerTarget) return;
    const target = modules.find((m) => m.id === composerTarget.moduleId);
    if (!target) return;
    setPrefill({ word: composerTarget.word, moduleId: composerTarget.moduleId });
    // Пришли «Добавить карточку» со страницы модуля — выбран этот модуль, а не первый.
    setPicked({ id: target.id, title: target.title });
    setComposerTarget(null);
  }, [composerTarget, modules]);
  useEffect(() => {
    if (!returnModuleId || !modulesQuery.data) return;
    const target = modules.find((m) => m.id === returnModuleId);
    if (target) {
      setPicked({ id: target.id, title: target.title });
      composerRef.current?.selectModule({ id: target.id, title: target.title });
    }
    setReturnModuleId(null);
  }, [returnModuleId, modules, modulesQuery.data]);

  // Экран — в адресе: «Назад» телефона возвращает с готовых наборов и списка модулей к словам.
  const openView = (next: View, extra?: Record<string, string>) => {
    if (next === view && !extra) return;
    trackEvent('words_view_opened', { view: next });
    setParams(next === 'ready' ? { tab: 'ready' } : next === 'modules' ? { view: 'modules', ...extra } : {});
  };
  // «Создать модуль» — как «+ Новый модуль» в чипе строки: модуль создаётся в модалке и выбирается в строке.
  const createModule = () => composerRef.current?.createModule();

  // --- Выбранный модуль (владелец 2026-10-28): слева (на телефоне — на полке) выбираешь модуль, справа (ниже) — его
  // слова, и новые карточки создаются в него — модуль строки создания и выбранный модуль одно и то же («неочевидно,
  // что „Задания дня“ внизу — модуль, в который создаётся карточка»): смена модуля в строке выбирает его и здесь.
  // Модуль выбран всегда («выбраны все слова, а в инпуте — в модуль „Задания дня“»): «Всех слов» без модуля нет, и
  // повторное нажатие выбор не снимает. По умолчанию — первый модуль списка («выбирай сразу первый по дефолту»), и
  // строка создания ставится в него; модуль пропал из списка (удалён) — снова первый.
  const pickedModule = picked && modulesQuery.data && !modules.some((m) => m.id === picked.id) ? null : picked;
  useEffect(() => {
    // Переход «Добавить карточку» со страницы модуля и возврат из тренировки выберут свой модуль — первый не ставим.
    if (pickedModule || composerTarget || returnModuleId || !modulesQuery.data) return;
    const first = modulesQuery.data.items[0];
    if (!first) return;
    setPicked({ id: first.id, title: first.title });
    composerRef.current?.selectModule({ id: first.id, title: first.title });
  }, [modulesQuery.data, pickedModule, composerTarget, returnModuleId]);
  const pickModule = (m: { id: string; title: string }) => {
    if (pickedModule?.id === m.id) return;
    setPicked({ id: m.id, title: m.title });
    composerRef.current?.selectModule({ id: m.id, title: m.title });
    trackEvent('words_module_selected', { surface: isMobile ? 'phone' : 'desktop' });
  };
  // Поиск слова — в плашке над колонками (компьютер).
  const [wordSearch, setWordSearch] = useState('');
  const [wordQuery, setWordQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setWordQuery(wordSearch.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [wordSearch]);

  // --- Добавленное сегодня и ещё не тренированное (владелец 2026-10-28): подсветка слов, «+N» у модуля, записи
  // «Сегодня» и «Тренировать новые» — из одного ответа сервера. Не пропадает после обновления страницы и уходит,
  // когда слова потренированы; прежние значения слова, поднятые повторным вводом, сюда не входят.
  const visit = useGenerationWatch((st) => st.visit);
  const entries = useGenerationWatch((st) => st.entries);
  const fresh = useFreshWordsQuery().data;
  const freshModules = useMemo(() => freshCounts(fresh), [fresh]);
  const freshIds = useMemo(() => freshStateSet(fresh), [fresh]);
  // Готовые карточки нажатия — сразу в «Словах», в сводке модулей и в добавленном сегодня: перечитать, когда у
  // записей этого захода меняется статус или число готовых.
  const freshKey = Object.values(entries)
    .filter((entry) => entry.visit === visit && entry.summary)
    .map((entry) => `${entry.id}:${entry.summary?.status}:${entry.summary?.counts.ready}`)
    .join('|');
  useEffect(() => {
    if (!freshKey) return;
    void qc.invalidateQueries({ queryKey: wordKeys.mineRoot });
    void qc.invalidateQueries({ queryKey: ['sessions', 'overview'] });
  }, [freshKey, qc]);

  // «Тренировать новые» — одна кнопка на добавленное сегодня и не тренированное в выбранном модуле (владелец
  // 2026-10-28: «тут должна была быть общая кнопка для новых слов»): разовой тренировкой без финального матча, как
  // «Задание дня». Тренировка собирается в пределах модуля — слова строка создания кладёт в выбранный модуль.
  const training = useRef(false);
  const freshHere = pickedModule ? (freshModules[pickedModule.id] ?? 0) : 0;
  const trainFresh = useCallback(
    async (moduleId: string) => {
      if (training.current) return;
      training.current = true;
      try {
        // Перед стартом — свежий ответ: слова могли потренировать в другой вкладке.
        const latest = await qc.fetchQuery({ queryKey: wordKeys.fresh, queryFn: fetchFreshWords, staleTime: 0 });
        const stateIds = latest.byModule[moduleId] ?? [];
        if (stateIds.length === 0) {
          navigate(buildCramPath(moduleId));
          return;
        }
        await startCramSession(moduleId, 'builder', {
          randomMix: true,
          stateIds,
          withBoss: false,
          limit: stateIds.length,
        });
        trackEvent('words_fresh_training_started', { words: stateIds.length, surface: isMobile ? 'phone' : 'desktop' });
        navigate(`${paths.session}?moduleId=${encodeURIComponent(moduleId)}`, {
          state: { cram: true, fromModuleId: moduleId, fromWords: true },
        });
      } catch {
        toast({ tone: 'error', message: t('words.trainError') });
      } finally {
        training.current = false;
      }
    },
    [isMobile, navigate, qc, t],
  );

  // «Сегодня» — записи выбранного модуля: нажатия этого захода, идущие и сегодняшние, где осталось что тренировать
  // (`keepIds`). Над «Словами», а на других экранах страницы — вверху. Смонтирована на любом экране: по нажатию сигнала
  // у строки она раскрывает запись (ТЗ главной § 8). Справа от заголовка — общая «Тренировать новые» (без числа —
  // владелец 2026-10-28).
  const generations = (
    <GenerationsSection
      isMobile={isMobile}
      dockRef={dockRef}
      onReenter={setPrefill}
      composerLocked={!composerOpen}
      scope="visit"
      keepIds={fresh?.generationIds}
      moduleId={pickedModule?.id ?? null}
      headerAction={
        pickedModule && freshHere > 0 ? (
          <button
            type="button"
            onClick={() => void trainFresh(pickedModule.id)}
            className="inline-flex min-h-[40px] flex-none items-center gap-2 rounded-pill bg-accent px-4 text-[14px] font-semibold text-fg-inv transition-colors hover:bg-accent-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2 focus-visible:ring-offset-base"
          >
            <PlayIcon className="h-3.5 w-3.5" aria-hidden />
            {t('generations.action.train')}
          </button>
        ) : null
      }
    />
  );

  return (
    // Корень страницы — предок липкой строки создания (ТЗ главной § 4–5). У него и у любой обёртки между строкой и
    // main не должно быть transform, filter, backdrop-filter, opacity < 1, will-change, contain, container-type,
    // isolation и прочего; overflow — только visible или clip. Поэтому анимация появления — только у колонки контента,
    // а строка стоит её соседом под распоркой. Поля колонки — из isMobile, как у строки, а не через nav: (900 px).
    <div className="flex min-h-full flex-col">
      <div className={cn('w-full animate-fadeIn', isMobile ? 'px-4 sm:px-6' : 'px-12')}>
        <div className={cn('mx-auto max-w-[1200px]', isMobile ? 'pt-16' : 'pt-10')}>
          {view === 'main' ? (
            <header ref={headRef} className={isMobile ? 'mb-4' : 'mb-6'}>
              <h1
                className={cn(
                  'text-balance font-serif font-medium tracking-[-0.02em]',
                  isMobile ? 'text-[30px] leading-[1.1] sm:text-[36px]' : 'text-[44px] leading-[53px]',
                )}
              >
                {t('words.title')}
              </h1>
              {/* Телефон: вместо подзаголовка — прогресс одной строкой, чтобы «Слова» начинались на первом экране. */}
              {isMobile ? (
                stats.data && plan ? (
                  <ProgressStrip plan={plan} accuracy={stats.data.accuracy} trainedThisWeek={stats.data.weekDays.some(Boolean)} phone />
                ) : (
                  <p className="mt-2 text-sm leading-relaxed text-muted">{composerOpen ? t('words.sub') : t('words.subLocked')}</p>
                )
              ) : null}
            </header>
          ) : (
            <header ref={headRef} className={isMobile ? 'mb-4' : 'mb-6'}>
              <button
                type="button"
                onClick={() => openView('main')}
                className="-ml-1 mb-2 inline-flex min-h-[44px] items-center gap-1 rounded-pill px-1 text-[14px] font-medium text-muted outline-none hover:text-fg focus-visible:ring-2 focus-visible:ring-accent"
              >
                <ChevronLeftIcon aria-hidden className="h-4 w-4" />
                {t('words.title')}
              </button>
              <h1
                className={cn(
                  'text-balance font-serif font-medium tracking-[-0.02em]',
                  isMobile ? 'text-[30px] leading-[1.1] sm:text-[36px]' : 'text-[44px] leading-[53px]',
                )}
              >
                {t(view === 'ready' ? 'words.readyTitle' : 'words.modulesTitle')}
              </h1>
            </header>
          )}
          {/* Просьба о почте — плашкой вверху (решение владельца 2026-10-21); строка создания внизу видна, но
              неактивна, с подписью «Откроется после подтверждения почты». */}
          {/* Пока не потрачена бесплатная карточка (2026-11-02), строка открыта — просьба о почте не спорит с ней. */}
          {user && !user.emailVerified && !user.guestCardAvailable && (
            <EmailVerifyBanner
              variant="dock"
              stacked={isMobile}
              email={user.email}
              className={isMobile ? 'mb-3' : 'mb-5'}
              onResend={() => trackEvent('home_composer_locked_action', { reason: 'email', action: 'resend' })}
            />
          )}
          {/* Бесплатная карточка ещё не потрачена — сказать заранее, что без почты она одна (тестировщик 2026-11-02:
              иначе её тратят на пробное слово и не знают, почему строка закрылась). */}
          {user && !user.emailVerified && user.guestCardAvailable && (
            <p className={cn('flex items-start gap-2 text-[14px] leading-5 text-muted', isMobile ? 'mb-3' : 'mb-5')}>
              <span aria-hidden className="flex-none text-[16px] leading-5 text-warn">
                ✉
              </span>
              <span>{t('words.guestCardHint')}</span>
            </p>
          )}
        </div>
      </div>
      {/* Строка создания — под заголовком, липкая к верху (владелец 2026-10-28: «снизу не особо удобно… давай наверх,
          чтобы не скроллилась»). Соседом колонок, а не внутри: у предков липкой полосы не должно быть transform. */}
      <HomeComposer
        ref={composerRef}
        placement="top"
        isMobile={isMobile}
        heroRef={headRef}
        dockRef={dockRef}
        modules={modulesQuery}
        tourActive={false}
        prefill={prefill}
        onPrefillDone={() => setPrefill(null)}
        coveredByPage={isMobile && moreOpen}
        heading={t('words.composerTitle')}
        onModuleChange={(m) => setPicked({ id: m.id, title: m.title })}
      />
      <div className={cn('w-full animate-fadeIn', isMobile ? 'px-4 sm:px-6' : 'px-12')}>
        <div className={cn('mx-auto max-w-[1200px]', isMobile ? 'pb-28 pt-4' : 'pb-12 pt-5')}>
          {view === 'main' && !isMobile && (
            <WordsToolbar
              module={pickedModule}
              cards={pickedModule ? (modules.find((m) => m.id === pickedModule.id)?.words ?? null) : null}
              overall={plan}
              search={wordSearch}
              onSearch={setWordSearch}
              onOpenReady={() => openView('ready')}
              onCreateModule={createModule}
            />
          )}

          {view === 'main' && isMobile && (
            <>
              <ModulesShelf
                modules={modules}
                loading={modulesQuery.isPending}
                fresh={freshModules}
                selectedId={pickedModule?.id ?? null}
                onSelect={pickModule}
                onCreateModule={createModule}
                onOpenAll={() => openView('modules')}
                onOpenReady={() => openView('ready')}
                onOpenDeleted={() => openView('modules', { filter: 'deleted' })}
              />
              <div className="mt-6">
                <WordsSection isMobile top={generations} freshIds={freshIds} module={pickedModule} />
              </div>
            </>
          )}

          {view === 'main' && !isMobile && (
            // Узкий компьютер: колонка модулей уже, а до 1024 px окна модули — над словами, иначе таблице слов не
            // хватает места.
            <div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-6 xl:grid-cols-[minmax(300px,380px)_minmax(0,1fr)] xl:gap-10">
              <section aria-labelledby="words-modules-col">
                {/* «Создать модуль» — в плашке сверху. */}
                <div className="mb-1 flex min-h-[44px] items-center">
                  <h2 id="words-modules-col" className="text-[13px] font-semibold uppercase tracking-[0.12em] text-muted">
                    {t('words.shelf.title')}
                  </h2>
                </div>
                <MyModules
                  onCreateModule={createModule}
                  selectedId={pickedModule?.id ?? null}
                  onSelect={pickModule}
                  fresh={freshModules}
                />
              </section>
              <WordsSection
                isMobile={false}
                top={generations}
                freshIds={freshIds}
                module={pickedModule}
                externalQuery={wordQuery}
              />
            </div>
          )}

          {view === 'modules' && (
            <>
              <div className="mb-4 flex justify-end">
                <Button variant="outline" size="sm" onClick={createModule} className="inline-flex items-center gap-1.5">
                  <PlusIcon className="h-4 w-4" aria-hidden />
                  {t('units.createModule')}
                </Button>
              </div>
              {generations}
              <MyModules onCreateModule={createModule} />
              <ReadyLink onClick={() => openView('ready')} className="mt-2" />
            </>
          )}

          {view === 'ready' && (
            <>
              {generations}
              {/* Каталог — та же разметка, что была на «Публичных модулях»: обёртка .lb даёт ему шрифты и переменные,
                  а модификатор снимает поля страницы. */}
              <div className="lb lb--embed">
                <LibraryGrid />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** «Готовые наборы слов ›» — каталог открывается своим экраном страницы. */
function ReadyLink({ onClick, className }: { onClick: () => void; className?: string }): ReactNode {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex min-h-[56px] w-full items-center gap-3 rounded-[18px] border border-dashed border-[#3a3a40] px-4 text-left text-[14px] text-[#cfcac3] outline-none transition-colors hover:text-fg focus-visible:ring-2 focus-visible:ring-accent',
        className,
      )}
    >
      <LibraryIcon aria-hidden className="h-5 w-5 flex-none text-muted" />
      <span className="min-w-0 flex-1">{t('words.readyLink')}</span>
      <ChevronRightIcon aria-hidden className="h-4 w-4 flex-none text-muted" />
    </button>
  );
}
